
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { AsyncAutocompleteSelect, Option } from "@/components/select/AsyncAutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import {
  ActiveDirectoryDriverUser,
} from "@/services/drivers-ad.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { driverCreationSchema } from "../schema/drivers.schema";
import { DriverTruckCard } from "./DriverTruckCard";
import {
  fetchActiveTrucksHandler,
  fetchDefaultADUsersHandler,
  getADOptionsHandler,
  handleUserSelectionHandler,
  loadADOptionsHandler,
  submitCreateDriverHandler,
} from "../schema/handler-functions";

type DriverCreationForm = {
  usuario?: Option | null;
  nombreApellido?: string;
  email?: string;
  equipos: { equipoId?: unknown }[];
};

export const DriverCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const {
    control,
    setValue,
    handleSubmit,
  } = useForm<DriverCreationForm>({
    resolver: zodResolver(driverCreationSchema),
    defaultValues: {
      usuario: null,
      nombreApellido: "",
      email: "",
      equipos: [{ equipoId: [] }],
    },
  });

  const watched = useWatch({ control });

  const {
    fields: truckFields,
    append: appendTruck,
    remove: removeTruck,
  } = useFieldArray({
    control,
    name: "equipos",
  });

  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingTrucks, setLoadingTrucks] = useState(false);
  const [truckOptions, setTruckOptions] = useState<{ label: string; value: string }[]>([]);

  const [loadingADUsers, setLoadingADUsers] = useState(false);
  const [defaultADUsers, setDefaultADUsers] = useState<ActiveDirectoryDriverUser[]>([]);

  const adUserById = useMemo(() => {
    const map = new Map<string, ActiveDirectoryDriverUser>();
    for (const u of defaultADUsers ?? []) {
      if (u?.id) map.set(String(u.id), u);
    }
    return map;
  }, [defaultADUsers]);

  useEffect(() => {
    if (!isActive) return;
    fetchActiveTrucksHandler({
      setLoadingTrucks,
      setTruckOptions,
      navigate,
      fetchPermissions,
    });
  }, [isActive]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadADOptions = (inputValue: string): Promise<Option[]> => {
    return loadADOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingADUsers,
      getADOptions: async (value) => getADOptionsHandler({ inputValue: value }),
    });
  };

  useEffect(() => {
    if (!isActive) return;
    fetchDefaultADUsersHandler({
      setLoadingADUsers,
      setDefaultADUsers,
    });
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.DRIVERS);
  };

  const handleAddTruck = () => {
    appendTruck({ equipoId: [] });
  };

  const hasDuplicateTruckSelected = useMemo(() => {
    const selectedValues = (watched?.equipos ?? [])
      .map((t) => {
        const raw = t?.equipoId;
        const selected = Array.isArray(raw) ? raw?.[0] : raw;
        const str = selected !== undefined && selected !== null ? String(selected) : "";
        return str.trim();
      })
      .filter(Boolean);

    return new Set(selectedValues).size !== selectedValues.length;
  }, [watched?.equipos]);

  const hasEmptyTruck = useMemo(() => {
    const trucks = watched?.equipos ?? [];
    return trucks.some((t) => {
      const raw = t?.equipoId;
      const selected = Array.isArray(raw) ? raw?.[0] : raw;
      return isEmpty(selected);
    });
  }, [watched?.equipos]);

  const hasNoFilledValues = useMemo(() => {
    const userId = watched?.usuario?.value;
    const name = watched?.nombreApellido;
    return !userId || isEmpty(name) || hasEmptyTruck;
  }, [watched?.usuario?.value, watched?.nombreApellido, hasEmptyTruck]);


  const onSubmit = async (values: DriverCreationForm) => {
    await submitCreateDriverHandler({
      values,
      setLoadingCreate,
      navigate,
      fetchPermissions,
    });
  };

  const defaultADOptions = useMemo(() => {
    return (
      defaultADUsers?.map((u) => ({
        label: `${u.displayName} - ${u.mail}`,          
        value: u.id,
      })) ?? []
    );
  }, [defaultADUsers]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      height="100%"
      minHeight={0}
      data-testid="drivers-creation-page"
    >
      <SectionHeader
        title="Maestro de fleteros"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        loading={loadingCreate}
        disableAdd={hasNoFilledValues || loadingCreate || hasDuplicateTruckSelected}
      />

      <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="16px">
        <Card.Root maxW="full" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Grid templateColumns="repeat(4, 1fr)" gap="32px">
              <GridItem colSpan={1}>
                <Field.Root>
                  <Field.Label>
                    <Text
                      fontWeight={600}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      Usuario (Active Directory)
                    </Text>
                  </Field.Label>
                  <AsyncAutocompleteSelect
                    control={control as any}
                    defaultOptions={defaultADOptions}
                    loadOptions={loadADOptions}
                    name="usuario"
                    placeholder="Seleccionar"
                    isLoading={loadingADUsers}
                    handleChange={(opt) =>
                      handleUserSelectionHandler({
                        opt,
                        adUserById,
                        setValue,
                      })
                    }
                    closeMenuOnSelect
                    triggerWithLength={false}
                    disabled={loadingCreate}
                    cacheOptions={false}
                  />
                </Field.Root>
              </GridItem>

              <GridItem colSpan={1}>
                <InputField
                  control={control as any}
                  inputName="nombreApellido"
                  labelText="Nombre"
                  placeholder="Ingresar"
                  required
                  disabled
                  minHeight="40px"
                />
              </GridItem>
            </Grid>
          </Card.Body>
        </Card.Root>

        {truckFields.map((field, index) => {
          return (
            <DriverTruckCard
              key={field.id}
              index={index}
              control={control as any}
              currentEquipo={(watched?.equipos ?? [])?.[index] as any}
              watchedEquipos={(watched?.equipos ?? []) as any}
              truckOptions={truckOptions}
              truckFieldsLength={truckFields.length}
              loadingTrucks={loadingTrucks}
              loadingCreate={loadingCreate}
              removeTruck={removeTruck}
              handleAddTruck={handleAddTruck}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};
